import { TextBox } from "../../../../components/text-box/text-box";
import { autoinject, transient } from "aurelia-dependency-injection";
import {
    UserModel,
    RoleUserModel,
    UserPhoneNumberModel,
    PersistUserRequest
} from "../../../../../models/manage/user.models";
import { ArrayUtils } from "../../../../../utilities/arrays";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { BaseClaims } from "../../../../../constants/authentication/base-claims";
import { ManageUsersClientService } from "../../../../../services/manage/manage-users.service";
import { UnitOfWorkObserver } from "../../../../../utilities/unit-of-work-observer";
import { BindingEngine, computedFrom } from "aurelia-binding";
import { NotificationType, NotificationClientService } from "../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { UserClientService } from "../../../../../services/authentication/user-client.service";
import { NotifyPropertyChangedBase, Reflection } from "../../../../../utilities/reflection";
import * as Enumerable from "linq";
import { ValidationsClientService } from "../../../../../services/validations/validations-client.service";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { Guid } from "../../../../../utilities/guid";

@autoinject
@transient()
export class ManageUser extends NotifyPropertyChangedBase {

    private _password: string = undefined;
    private _reload: () => void;
    private _close: () => void;

    public passVM: TextBox;
    public readonly defaults = {
        password: "0000000",
    }
    public setAutomatically: boolean = null;
    public canEditUser: boolean = true;
    public currentUser: UserModel;
    public userUow: UnitOfWorkObserver;
    public confirmedPassword: string = "";

    constructor(private userClaimsService: UserClaimsService,
        private manageUsersService: ManageUsersClientService,
        private engine: BindingEngine,
        private notificationService: NotificationClientService,
        private i18N: I18N,
        private userClientService: UserClientService,
        private validationsClientService: ValidationsClientService) {
        super();
        this.canEditUser = this.userClaimsService.hasPermission(BaseClaims.CanUpdateUsers);
    }

    @computedFrom(Reflection.getPropertyPath<ManageUser>(x => x._password))
    public get newPassword() {
        return this._password;
    }

    @computedFrom(Reflection.getPropertyPath<ManageUser>(x => x._password))
    public get password() {
        return this._password;
    }

    @computedFrom(Reflection.getPropertyPath<ManageUser>(x => x.changes.currentUser))
    public get excludedRoleIds() {
        return Enumerable.from(this.currentUser.roleUsers).select(x => x.roleId).toArray();
    }

    public rolesSelectedChanged() {
        this.onPropertyChanged<ManageUser>(x => x.currentUser);
    }

    public set password(val: string) {
        if (val === null) {
            this.setAutomatically = null;
        } else if (this.currentUser && val == this.defaults.password && this.setAutomatically === null) {
            this.setAutomatically = true;
        }
        else if (this.setAutomatically) {
            this.setAutomatically = false;

            if (val.length === this.defaults.password.length + 1) {
                this.passVM.clearField();

                const defaultPassword: string = this.defaults.password;

                for (let i = 0; i < val.length; i++) {
                    if (i >= defaultPassword.length || defaultPassword.charAt(i) !== val.charAt(i)) {
                        val = val.charAt(i);
                        break;
                    }
                }
            }
        }
        this._password = val;
    }

    public userHasChanged(newValue: UserModel): void {
        this._password = null;
        this.setAutomatically = null;
        this.confirmedPassword = "";
        if (newValue && newValue.userId > 0 && newValue.hasPasswordSet) {
            this.password = this.defaults.password;
        } else {
            this.password = null;
        }
        if (newValue && this.currentUser.roleUsers) {
            if (this.currentUser.userId === 0) {
                this.currentUser.hasPasswordSet = false;
            }
        }
    }

    public removeRole(item: RoleUserModel): void {
        ArrayUtils.remove(this.currentUser.roleUsers, item);
        this.onPropertyChanged<ManageUser>(x => x.currentUser);
    }

    public addRole(): void {
        this.currentUser.roleUsers.push({
            dbId: 0,
            insertDate: new Date().toJSON(),
            isDefault: this.currentUser.roleUsers.length == 0,
            roleId: 0,
            updateDate: new Date().toJSON(),
            userId: this.currentUser.userId,
            entityType: EntityTypes.RoleUser,
            identifier: Guid.generateGuid()
        });
    }

    public addContactNumber(): void {
        const userContactNumber: UserPhoneNumberModel = {
            userId: this.currentUser.userId,
            phoneNumber: null,
            phoneNumberTypeId: -1,
            phoneSequence: 0,
            phoneID: 0,
            entityType: EntityTypes.UserPhoneNumber,
            identifier: Guid.generateGuid(),
        };
        this.currentUser.userPhoneNumbers.push(userContactNumber);
    }

    public removeContactNumber(item: UserPhoneNumberModel): void {
        ArrayUtils.remove(this.currentUser.userPhoneNumbers, item);
    }

    public areThereEmptyRoles() {
        return Enumerable.from(this.currentUser.roleUsers).any(x => !x.roleId);
    }

    public save() {
        return new Promise<void>((resolve, reject) => {
            if (this.userUow && this.userUow.hasChanges()) {
                const changes = this.userUow.getChanges();
                this.manageUsersService.save(<PersistUserRequest>{
                    changes: changes,
                    id: this.currentUser.userId,
                    newPassword: this.setAutomatically ? null : this.newPassword,
                    passwordConfirmation: this.setAutomatically ? "" : this.confirmedPassword
                }).then(x => {
                    if (x.validationResult && x.validationResult.hasError) {
                        this.validationsClientService.showValidationErrors(x.validationResult);
                        reject();
                    }
                    else {
                        this.notificationService.show(this.i18N.tr('user-successfully-saved-text'), "", NotificationType.Success);
                        const currentUser = this.userClientService.currentUser;
                        if (this.userClientService.currentUser.id == this.currentUser.userId && currentUser && this.currentUser.roleUsers.every(x => x.roleId != currentUser.roleId)) {
                            location.reload();
                        } else {
                            this.userUow.clearChanges();
                            this.setAutomatically = true;
                            this._reload();
                        }
                        resolve();
                    }
                }).catch(() => {
                    this.notificationService.show(this.i18N.tr('user-cannot-be-saved-text'), "", NotificationType.Error);
                    reject();
                });
            }
            else {
                this.notificationService.show(this.i18N.tr('no-changes-performed-text'), this.i18N.tr('there-were-no-changes-to-save-text'), NotificationType.Info);
                resolve();
            }
        });
    }

    cancel() {
        this._close();
    }

    areThereUnsavedChanges() {
        if(!this.userUow){
            return false;
        }

        const changes = this.userUow.getChanges();

        if (!this.setAutomatically) return true;

        if (!this.userUow.getHasChanges || (Object.keys(changes).length == 1 && changes['identifier'])) {
            return false;
        }
        else {
            return true;
        }
    }

    public attached(): void {
        if (this.currentUser && this.currentUser.userId > 0 && this.currentUser.hasPasswordSet && this.passVM) {
            this.passVM.value = this.defaults.password;
        }
    }

    activate(model: any) {
        this._reload = model.reload;
        this._close = model.close;

        const initializeUow = () => {
            this.userHasChanged(this.currentUser);
            if (this.userUow) this.userUow.dispose();
            this.userUow = UnitOfWorkObserver.getTrackingInstance(this.engine, this.currentUser);
        }

        if (model.userId != 0) {
            this.manageUsersService.getUser(model.userId)
                .then(x => {
                    this.currentUser = x.user
                    initializeUow();
                });
        }
        else {
            this.currentUser = {
                email: "",
                firstName: "",
                lastName: "",
                fullName: "",
                isActive: false,
                isManager: false,
                isDefault: false,
                managerId: 0,
                sequenceNumber: 0,
                userId: 0,
                jobDescriptionId: 0,
                idNumber: null,
                branchId: 0,
                nationalId: 0,
                guid: "",
                entityType: EntityTypes.User,
                identifier: Guid.generateGuid(),
                identificationDocumentIssuerCountryId: null,
                userPhoneNumbers: [],
                roleUsers: [],
                permissions: []
            };
            initializeUow();
            this.currentUser.isActive = true;
            this.currentUser.branchId = model.branchId;
        }
    }

    detached() {
        this.userUow.dispose();
        this.userUow = null;
        this.currentUser = null;
        this.passVM = null;
        this._reload = null;
        this._close = null;
    }
}
